import { useEffect, useRef } from "react";
import { isNativeShell } from "./config";

/**
 * The bits of native behaviour the web build doesn't need: hide the launch
 * splash once React is up, and make Android's hardware back button do the
 * sensible thing instead of dumping a child out of the app mid-game.
 *
 * Everything is imported dynamically and only inside a native shell, so the
 * browser build never downloads any of it.
 */
export function useNativeShell(onBack: () => boolean): void {
	const handler = useRef(onBack);
	handler.current = onBack;

	useEffect(() => {
		if (!isNativeShell) return;

		let cleanup: (() => void) | undefined;
		let cancelled = false;

		void (async () => {
			try {
				const [{ App }, { SplashScreen }] = await Promise.all([
					import("@capacitor/app"),
					import("@capacitor/splash-screen"),
				]);
				await SplashScreen.hide();
				if (cancelled) return;

				const listener = await App.addListener("backButton", () => {
					// `false` means "nothing left to back out of" — then it's a quit.
					if (!handler.current()) void App.exitApp();
				});
				if (cancelled) {
					void listener.remove();
					return;
				}
				cleanup = () => void listener.remove();
			} catch {
				// An older shell without these plugins still plays fine.
			}
		})();

		return () => {
			cancelled = true;
			cleanup?.();
		};
	}, []);
}
